import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { getMessageFileUrl } from '../../../services/messagingService';

const MessageBubble = ({ message, isSender, currentUserId }) => {
  const formatTime = (date) => {
    return new Date(date)?.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleDownloadFile = async (attachment) => {
    try {
      const { data: url } = await getMessageFileUrl(attachment?.file_path);
      if (url) {
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return 'Image';
    if (fileType?.startsWith('video/')) return 'Video';
    if (fileType?.startsWith('audio/')) return 'Music';
    if (fileType?.includes('pdf')) return 'FileText';
    return 'File';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  return (
    <div className={`flex gap-3 ${isSender ? 'flex-row-reverse' : 'flex-row'}`}>
      <div className="flex-shrink-0">
        <div className="w-8 h-8 rounded-full overflow-hidden bg-muted">
          <Image
            src={message?.sender?.avatar_url || '/assets/images/no_image.png'}
            alt={`${message?.sender?.full_name || 'User'} avatar`}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <div className={`flex flex-col gap-1 max-w-[70%] ${isSender ? 'items-end' : 'items-start'}`}>
        <div
          className={`rounded-lg px-4 py-2 ${
            isSender
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap break-words">{message?.content}</p>

          {/* Attachments */}
          {message?.attachments?.length > 0 && (
            <div className="mt-2 space-y-2">
              {message?.attachments?.map((attachment) => (
                <button
                  key={attachment?.id}
                  onClick={() => handleDownloadFile(attachment)}
                  className={`w-full p-3 rounded border ${
                    isSender
                      ? 'border-primary-foreground/20 hover:bg-primary-foreground/10'
                      : 'border-border hover:bg-background'
                  } transition-smooth`}
                >
                  <div className="flex items-center gap-3">
                    <Icon 
                      name={getFileIcon(attachment?.file_type)} 
                      size={20} 
                      className={isSender ? 'text-primary-foreground' : 'text-foreground'}
                    />
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium truncate">{attachment?.file_name}</p>
                      <p className={`text-xs ${
                        isSender ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}>
                        {formatFileSize(attachment?.file_size)}
                      </p>
                    </div>
                    <Icon 
                      name="Download" 
                      size={16} 
                      className={isSender ? 'text-primary-foreground' : 'text-muted-foreground'}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`text-xs ${
              isSender ? 'text-muted-foreground' : 'text-muted-foreground'
            }`}
          >
            {formatTime(message?.created_at)}
          </span>
          {isSender && message?.status && (
            <Icon
              name={message?.status === 'read' ? 'CheckCheck' : 'Check'}
              size={14}
              className="text-muted-foreground"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;